import { useDebouncedState } from '@react-hookz/web';
import { useCallback, useState } from 'react';

/**
 * A hook that combines a useState and a useDebouncedState.
 *
 * The eager value is the value that is set immediately, and the debounced value is the value that is set after a delay.
 *
 * @param args - The arguments to pass to useDebouncedState
 * @returns An object with the eager and debounced values, and a function to set the value
 *
 * @example
 * const { eagerValue: inputValue, debouncedValue: apiSearchValue, setValue } = useEagerAndDebouncedState('initialValue', 150);
 * const { data, isLoading } = useQuery({
 *   queryKey: ['search', apiSearchValue],
 *   queryFn: () => fetchSearchResults(apiSearchValue),
 * });
 *
 * return (
 *   <div>
 *     <Input value={inputValue} onChange={(e) => setValue(e.target.value)} />
 *     {data && <p>{data}</p>}
 *   </div>
 * )
 */
export function useEagerAndDebouncedState<T>(
  ...args: Parameters<typeof useDebouncedState<T>>
) {
  const [initialState, delay, maxWait] = args;
  const [eagerValue, setEagerValue] = useState<T>(initialState);
  const [debouncedValue, setDebouncedValue] = useDebouncedState<T>(
    initialState,
    delay,
    maxWait,
  );

  const setValue = useCallback(
    (value: T) => {
      setEagerValue(value);
      setDebouncedValue(value);
    },
    [setEagerValue, setDebouncedValue],
  );

  return {
    eagerValue,
    debouncedValue,
    setValue,
  };
}
